import { CompletedTag } from "components/Tags/CompletedTag";
import { TimeTag } from "components/Tags/TimeTag";
import { Typography } from "components/Typography";
import { VimeoPlayer } from "components/VimeoPlayer";
import { RESPONSE_MESSAGES } from "constants/components";
import { responseMessage } from "helpers/errorHandling.helper";
import { useModal } from "hooks/useModal";
import { ModalType } from "models/common";
import React, { useCallback, useState } from "react";
import { saveVideoProgress } from "services/guidance.service";
import { VideoConsentPopup } from "./VideoConsentPopup/VideoConsentPopup";
import styles from "./VideoWithThumbnail.module.scss";

// Define the props type for the component.
export interface VideoWithThumbnailProps {
  isCompletedStatusNeeded: boolean;
  data: {
    url: string; // Unique video ID (if needed for tracking or playback).
    thumbnailUrl: string; // URL for the video's thumbnail image.
    title: string; // Title of the video.
    duration: string; // Duration of the video.
    isCompleted?: boolean;
    articleId?: string;
    video_id?: string;
  };
}

const hasTargetingConsent = (): boolean => {
  return !!window.OnetrustActiveGroups?.includes("C0004");
};

export const VideoWithThumbnail: React.FC<VideoWithThumbnailProps> = ({
  isCompletedStatusNeeded,
  data: {
    url,
    thumbnailUrl,
    title,
    duration,
    isCompleted,
    articleId,
    video_id,
  },
}) => {
  // State to track whether the video is playing or not.
  const [isPlaying, setIsPlaying] = useState(false);
  const { showModal, closeModal } = useModal();

  const playVideo = useCallback(async () => {
    setIsPlaying(true);
    try {
      const payload = {
        article_id: articleId,
        video_id: video_id,
      };
      if (isCompletedStatusNeeded && !isCompleted)
        await saveVideoProgress(payload);
    } catch (error) {
      responseMessage(RESPONSE_MESSAGES.ERROR);
      console.error("Error saving video progress:", error);
    }
  }, [articleId, video_id, isCompletedStatusNeeded, isCompleted]);

  // Handler for the play button click — checks cookie consent before playing.
  const handlePlayClick = useCallback(() => {
    if (hasTargetingConsent()) {
      playVideo();
      return;
    }
    // MARK: video consent popup
    const modalId = showModal({
      type: ModalType.NORMAL,
      children: (
        <VideoConsentPopup
          onAccept={() => {
            window.OneTrust?.UpdateConsent("Category", "C0004:1");
            closeModal(modalId);
            playVideo();
          }}
          onReject={() => {
            closeModal(modalId);
          }}
        />
      ),
      showFooter: false,
      closable: true,
    });
  }, [showModal, closeModal, playVideo]);

  return (
    <div className={styles.video_container}>
      {/* Render thumbnail if video is not playing */}
      {!isPlaying ? (
        <div className={styles.image_container}>
          <div className={styles.section_top}>
            <Typography variant="h5" title={title} className={styles.title}>
              {title}
            </Typography>
            <TimeTag duration={duration} />
          </div>
          {/* Thumbnail Image */}
          <img
            src={thumbnailUrl} // Use the thumbnail URL provided by the data prop.
            alt={title} // Use the video title as the alt text for accessibility.
            className={styles.image}
          />
          {/* Play Icon: Click to play the video */}
          <div
            onClick={handlePlayClick}
            className={styles.play_icon}
            role="button"
            tabIndex={0}
            aria-label="video-play"
          >
            {/* Unicode Play Icon */}
          </div>
          {isCompleted && (
            <div className={styles.section_bottom}>
              <CompletedTag />
            </div>
          )}
        </div>
      ) : (
        // Render the video player when the video is playing
        <div className={styles.video_player}>
          <VimeoPlayer url={url} autoPlay={true} />
        </div>
      )}
    </div>
  );
};
